import { FC } from 'react';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { Stack, Typography } from '@mui/material';

import { LocalizationTypography } from '~components/organisms/localization';

const Tile: FC<{ value: React.ReactNode; labelId: string; label: string }> = ({
  value,
  labelId,
  label,
}) => (
  <Stack
    gap={0.5}
    sx={{
      flex: 1,
      bgcolor: '#F3F4F6',
      borderRadius: 1.5,
      px: 2,
      py: 1.5,
      minWidth: 0,
    }}
  >
    {value}
    <LocalizationTypography
      textId={labelId}
      defaultText={label}
      variant="Poppins/Body-md"
      color="#6B7280"
    />
  </Stack>
);

interface PassUsageStatsProps {
  usedChargers: number;
  /** `-1` means unlimited — rendered as ∞ rather than a number. */
  swapsLeft: number;
  /** Greys the figures out once the pass is no longer active. */
  muted?: boolean;
}

/**
 * "3 Swaps Used" / "∞ Remaining" — read straight off the running period.
 *
 * `swapsLeft` is authoritative: `maxSwaps - usedChargers` is off by one.
 */
export const PassUsageStats: FC<PassUsageStatsProps> = ({
  usedChargers,
  swapsLeft,
  muted = false,
}) => {
  const usedColor = muted ? '#6B7280' : '#16A34A';
  const leftColor = muted ? '#6B7280' : 'brand.dark';

  return (
    <Stack gap={1.5} width="100%">
      <LocalizationTypography
        textId="app.rentalPage.newStepThree.pass.usageStats"
        defaultText="Usage Stats"
        variant="Poppins/Heading-4"
      />
      <Stack direction="row" gap={1.5}>
        <Tile
          value={
            <Typography variant="Poppins/Heading-3" color={usedColor}>
              {usedChargers}
            </Typography>
          }
          labelId="app.rentalPage.newStepThree.pass.swapsUsed"
          label="Swaps Used"
        />
        <Tile
          value={
            swapsLeft === -1 ? (
              <AllInclusiveIcon sx={{ color: leftColor, fontSize: 32 }} />
            ) : (
              <Typography variant="Poppins/Heading-3" color={leftColor}>
                {swapsLeft}
              </Typography>
            )
          }
          labelId="app.rentalPage.newStepThree.pass.swapsRemaining"
          label="Remaining"
        />
      </Stack>
    </Stack>
  );
};
